import React, { useMemo, useState } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { ANChartJSWrapperV2, createDefaultTooltipCard } from "@answersnow/pkg-theme";
import type {
  ChartVariant,
  LegendConfig,
  LegendItem,
  MarkerStyle,
  TooltipConfig,
  TooltipRow,
} from "@answersnow/pkg-theme";
import type { ChartDataset, Plugin } from "chart.js";

import { getAssessmentDefinition } from "../assessmentConfig";
import { space5, space6 } from "../styled";
import { type AssessmentRecord, type AssessmentType } from "../types";
import { buildAssessmentChartPayload } from "../util/assessmentChartData.utils";
import AssessmentChartLegendRow from "./AssessmentChartLegendRow";

/**
 * Creates a tooltip card identical to `createDefaultTooltipCard` but uses
 * regular font weight (400) instead of bold for all text.
 */
function createLightTooltipCard(title: string, rows: TooltipRow[]): HTMLElement {
  const card = createDefaultTooltipCard(title, rows);
  card.querySelectorAll<HTMLElement>("*").forEach((el) => {
    el.style.fontWeight = "400";
  });
  return card;
}

interface AssessmentChartProps {
  assessmentType: AssessmentType;
  dataAssessments: AssessmentRecord[];
}

export default function AssessmentChart({ assessmentType, dataAssessments }: AssessmentChartProps) {
  const definition = getAssessmentDefinition(assessmentType);

  // ── QABF: group records by topography and track active index ──────────────
  const qabfTopographies = useMemo(() => {
    if (assessmentType !== "qabf") return null;
    const map = new Map<string, AssessmentRecord[]>();
    dataAssessments.forEach((record) => {
      const topography = String(record.data.topography || "Unknown");
      if (!map.has(topography)) map.set(topography, []);
      map.get(topography)!.push(record);
    });
    return Array.from(map.entries()).map(([name, records]) => ({ name, records }));
  }, [assessmentType, dataAssessments]);

  const [qabfIndex, setQabfIndex] = useState(0);

  const activeAssessments = useMemo(() => {
    if (assessmentType !== "qabf" || !qabfTopographies?.length) return dataAssessments;
    return (
      qabfTopographies[Math.min(qabfIndex, qabfTopographies.length - 1)]?.records ?? dataAssessments
    );
  }, [assessmentType, dataAssessments, qabfTopographies, qabfIndex]);

  // ── Chart data ────────────────────────────────────────────────────────────
  const chartPayload = useMemo(() => {
    const configuredDatasets = definition.chart.datasets as unknown as ChartDataset<
      "line" | "bar",
      Array<number | null>
    >[];

    return buildAssessmentChartPayload(assessmentType, activeAssessments, configuredDatasets);
  }, [assessmentType, activeAssessments, definition.chart.datasets]);

  const chartVariant = useMemo<ChartVariant>(() => {
    if (definition.chart.variant === "stackedMixed") {
      return "mixed";
    }

    return definition.chart.variant;
  }, [definition.chart.variant]);

  // ── Axes ──────────────────────────────────────────────────────────────────
  const yAxis = useMemo(
    () => ({
      label: definition.chart.yAxisLabels?.[0] || "Score",
      min: chartPayload.min,
      max: chartPayload.max,
    }),
    [chartPayload.max, chartPayload.min, definition.chart.yAxisLabels]
  );

  const yAxis2 = useMemo(() => {
    if (chartVariant !== "mixed") {
      return undefined;
    }

    // When bar and line datasets have separate axes, set bar (y1) max = 2× bar data max.
    // This pushes bars into the bottom half of the chart area, visually below the line.
    if (chartPayload.barMax !== undefined) {
      return {
        label: definition.chart.yAxisLabels?.[1] || "Score",
        min: 0,
        max: chartPayload.barMax * 2,
      };
    }

    return {
      label: definition.chart.yAxisLabels?.[1] || "Score",
      min: chartPayload.min,
      max: chartPayload.max,
    };
  }, [
    chartPayload.barMax,
    chartPayload.max,
    chartPayload.min,
    chartVariant,
    definition.chart.yAxisLabels,
  ]);

  // ── Hide dots normally; show on hover ────────────────────────────────────
  const chartOptions = useMemo(
    () => ({
      datasets: {
        line: {
          pointRadius: 0,
          pointHoverRadius: 4,
        },
      },
    }),
    []
  );

  // ── Custom guide line plugin: uses theme divider color (grey.200 = #CDD3DA) ─
  const guideLinePlugin = useMemo<Plugin<"line" | "bar">>(
    () => ({
      id: "assessmentGuideLine",
      beforeDatasetsDraw(chart) {
        const { tooltip } = chart;
        if (!tooltip || tooltip.opacity === 0 || !tooltip.dataPoints?.length) return;
        const x = tooltip.dataPoints[0].element.x;
        const {
          ctx,
          chartArea: { top, bottom },
        } = chart;
        const isBarLikeChart = chart.getDatasetMeta(0)?.type === "bar";
        ctx.save();
        if (isBarLikeChart) {
          const xScale = chart.scales.x;
          const dataIndex = tooltip.dataPoints[0].dataIndex;
          const labelsLength = chart.data.labels?.length ?? 0;
          const currentX = xScale.getPixelForValue(dataIndex);
          const prevX = dataIndex > 0 ? xScale.getPixelForValue(dataIndex - 1) : undefined;
          const nextX =
            dataIndex < labelsLength - 1 ? xScale.getPixelForValue(dataIndex + 1) : undefined;
          const left = prevX !== undefined ? (prevX + currentX) / 2 : chart.chartArea.left;
          const right = nextX !== undefined ? (nextX + currentX) / 2 : chart.chartArea.right;
          ctx.fillStyle = "#CDD3DA";
          ctx.globalAlpha = 0.25;
          ctx.fillRect(left, top, right - left, bottom - top);
          ctx.globalAlpha = 1;
        } else {
          ctx.beginPath();
          ctx.moveTo(x, top);
          ctx.lineTo(x, bottom);
          ctx.lineWidth = 1;
          ctx.strokeStyle = "#CDD3DA";
          ctx.stroke();
        }
        ctx.restore();
      },
    }),
    []
  );

  // ── Custom tooltip: append per-dataset suffix (e.g. "/9" or " / 16") ─────
  const tooltipRenderer = useMemo(() => {
    const suffixMap = new Map<string, string>();
    definition.chart.datasets.forEach((dataset) => {
      if (dataset.tooltipValueSuffix) {
        suffixMap.set(dataset.label, dataset.tooltipValueSuffix);
      }
    });

    if (suffixMap.size === 0) return undefined;

    return (title: string, rows: TooltipRow[]) => {
      const modified = rows.map((row) => {
        const suffix = suffixMap.get(row.label);
        return suffix ? { ...row, value: `${row.value}${suffix}` } : row;
      });
      return createLightTooltipCard(title, modified);
    };
  }, [definition.chart.datasets]);

  const tooltip = useMemo<TooltipConfig>(
    () => ({ mode: "single", renderer: tooltipRenderer ?? createLightTooltipCard }),
    [tooltipRenderer]
  );

  // ── Legend ────────────────────────────────────────────────────────────────
  const legend = useMemo<LegendConfig>(() => {
    const renderLegend = (items: LegendItem[]) => <AssessmentChartLegendRow items={items} />;

    if (chartVariant !== "mixed") {
      return {
        defaultMarker: (chartVariant === "line" ? "line" : "circle") as MarkerStyle,
        renderLegend,
      };
    }

    return {
      lineLabel: definition.chart.legendLineLabel,
      barLabel: definition.chart.legendBarLabel,
      renderLineLegend: renderLegend,
      renderBarLegend: renderLegend,
    };
  }, [chartVariant, definition.chart.legendLineLabel, definition.chart.legendBarLabel]);

  const hasTitle = definition.chart.title && definition.chart.description;

  return (
    <Stack m={space6}>
      {hasTitle && (
        <Box
          marginBottom={space5}
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "baseline" }}
          gap={1}
        >
          <Typography variant="subtitle1">{definition.chart.title}</Typography>
          <Typography variant="body2">{definition.chart.description}</Typography>
        </Box>
      )}

      {/* QABF topography navigation */}
      {assessmentType === "qabf" && qabfTopographies && qabfTopographies.length > 0 && (
        <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
          <IconButton
            size="small"
            onClick={() => setQabfIndex((i) => Math.max(0, i - 1))}
            disabled={qabfIndex === 0}
            aria-label="Previous topography"
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="subtitle2" mx={1}>
            {qabfTopographies[Math.min(qabfIndex, qabfTopographies.length - 1)].name}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setQabfIndex((i) => Math.min(qabfTopographies.length - 1, i + 1))}
            disabled={qabfIndex >= qabfTopographies.length - 1}
            aria-label="Next topography"
          >
            <ChevronRight />
          </IconButton>
        </Box>
      )}

      {/* No per-chart scroll/min-width here — the page-level container
          (Assessments/index.tsx) holds the Figma min width and scrolls as
          one unit instead of each chart scrolling independently. */}
      <section>
        {!chartPayload.hasData && (
          <Box
            sx={{
              height: 270,
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No score history yet.
            </Typography>
          </Box>
        )}

        {chartPayload.hasData && (
          <ANChartJSWrapperV2
            variant={chartVariant}
            data={chartPayload.data as Parameters<typeof ANChartJSWrapperV2>[0]["data"]}
            options={chartOptions}
            yAxis={yAxis}
            yAxis2={yAxis2}
            tooltip={tooltip}
            plugins={[guideLinePlugin]}
            legend={legend}
            height={450}
          />
        )}
      </section>
    </Stack>
  );
}
